import { useRef, useState } from 'react';
import { useDrag } from 'react-dnd';

import {
  Box,
  Button,
  ButtonGroup,
  Card,
  FormField,
  FormHelp,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  Label,
  Menu,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconDeleteOutline, IconMoreFilled, IconRenameFilled, IconReorderFilled } from '@zillow/constellation-icons';

import type { DocumentDetailsDto } from '@zg-rentals/ts-leasing-client';
import { LeaseType } from '@zg-rentals/ts-leasing-client';
import getDrop from './useDropHelper';
import LeaseModal from '../../../../components/Containers/LeaseModal';
import { CANCEL } from '../../../../constants/stringConstants';
import { FULLSTORY_EXCLUDE } from '../../../../constants/analytics';
import type { Lease } from '../../../../models';
import { useIsFetching, useIsMutating } from '@zg-rentals/react-query5';
import { useRenameLeaseDocumentsMutation } from '../../../../utils/queries';
import { useLeasingUrlParams } from '../../../../hooks/useLeasingUrlParams';

type Props = {
  canEdit: boolean;
  uploadedFileDetails: DocumentDetailsDto;
  onDelete(): void;
  index: number;
  onMoveFile(startIndex: number, targetIndex: number): void;
  onDropComplete?: (finalIndex: number) => void;
  lease: Lease;
  marginBottom?: string;
};

function UploadedLeaseControls({
  canEdit,
  onDelete,
  uploadedFileDetails,
  index,
  onMoveFile,
  onDropComplete,
  lease,
  marginBottom,
}: Props) {
  const { leaseId } = useLeasingUrlParams();

  const { name } = uploadedFileDetails;
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [documentName, setDocumentName] = useState(name);

  const isFetching = useIsFetching() > 0;
  const isMutating = useIsMutating() > 0;

  const renameLeaseDocumentsMutation = useRenameLeaseDocumentsMutation();

  function onDeleteClick() {
    setIsDeleting(true);
  }

  function onDeleteCancel() {
    setIsDeleting(false);
  }

  function onRenameClick() {
    setIsRenaming(true);
  }

  function onRenameCancel() {
    setIsRenaming(false);
  }

  function onRenameClose() {
    onRenameCancel();
  }

  async function onRenameSave() {
    await renameLeaseDocumentsMutation.mutateAsync({
      postData: {
        leaseId: leaseId as unknown as number,
        renames: [{ documentId: uploadedFileDetails.id, name: documentName }],
      },
    });
    setIsRenaming(false);
  }

  const { id } = uploadedFileDetails;
  const DND_TYPE_ID = 'File';

  const ref = useRef(null);
  const drop = getDrop(DND_TYPE_ID, ref, index, onMoveFile, onDropComplete);
  const [{ isDragging }, drag] = useDrag({
    item: { type: DND_TYPE_ID, id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  if (canEdit) {
    drag(drop(ref));
  }

  const dynamicStyles = {
    opacity: isDragging ? 0 : 1,
    cursor: canEdit ? 'move' : 'default',
  };

  const isSignedSelfUpload = lease?.leaseType === LeaseType.SIGNED_SELF_UPLOAD;

  return (
    <div ref={canEdit ? ref : null} style={dynamicStyles}>
      <LeaseModal
        id="delete-confirmation"
        onClose={onDeleteCancel}
        isOpen={isDeleting}
        header={<Heading level={1}>Delete your file?</Heading>}
        body={<Text>Any signatures or preparations for signing will be lost.</Text>}
        footer={
          <ButtonGroup aria-label="delete file">
            <TextButton onClick={onDeleteCancel} data-test="cancel-button" disabled={isFetching || isMutating}>
              {CANCEL}
            </TextButton>
            <Button
              tone="brand"
              emphasis="filled"
              loading={isFetching || isMutating}
              onClick={onDelete}
              data-test="trigger-on-delete-handler"
              disabled={isFetching}
            >
              Yes
            </Button>
          </ButtonGroup>
        }
      />
      <LeaseModal
        id="rename-confirmation"
        onClose={onRenameClose}
        isOpen={isRenaming}
        header={<Heading level={1}>Rename document</Heading>}
        body={
          <FormField
            className={FULLSTORY_EXCLUDE}
            label={<Label>Name</Label>}
            required
            disabled={isFetching || isMutating}
            control={<Input value={documentName} onChange={(e) => setDocumentName(e.target.value)} />}
            description={
              <FormHelp>
                {isSignedSelfUpload
                  ? 'Your tenant will see this file name when viewing the document'
                  : 'Your tenant will see this file name when signing the document'}
              </FormHelp>
            }
          />
        }
        footer={
          <ButtonGroup aria-label="rename document">
            <TextButton onClick={onRenameClose} data-test="cancel-button">
              {CANCEL}
            </TextButton>
            <Button
              tone="brand"
              emphasis="filled"
              onClick={onRenameSave}
              data-test="trigger-on-rename-handler"
              disabled={isFetching || isMutating || !documentName.trim()}
              loading={isFetching || isMutating}
            >
              Save
            </Button>
          </ButtonGroup>
        }
      />
      <Card
        tone="neutral"
        elevated={false}
        css={{ marginTop: index === 0 ? 0 : 'tight', marginBottom: marginBottom || undefined }}
      >
        <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tighter', alignItems: 'center' }}>
          <Box css={{ gridColumnEnd: 'span 1' }}>
            {canEdit && (
              <Icon role="img" size="md" css={{ color: 'text.accent.brand.hero' }}>
                <IconReorderFilled />
              </Icon>
            )}
          </Box>
          <Box
            css={{
              gridColumnEnd: { base: 'span 5', md: 'span 3' },
              height: '3rem',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Image
              alt="Document preview"
              src={uploadedFileDetails.imageUrl}
              css={{ objectFit: 'contain', maxHeight: '3rem', width: '100%' }}
            />
          </Box>
          <Box css={{ gridColumnEnd: { base: 'span 5', md: 'span 7' } }}>
            <TextButton asChild>
              <a href={uploadedFileDetails.downloadUrl}>{name}</a>
            </TextButton>
          </Box>
          <Box css={{ gridColumnEnd: 'span 1', justifySelf: 'end' }}>
            {canEdit && !isDragging && (
              <Menu
                trigger={
                  <IconButton
                    title="More"
                    emphasis="bare"
                    tone="brand"
                    shape="square"
                    onClick={(e) => e.stopPropagation()}
                    data-test="trigger-file-details-menu"
                  >
                    <Icon>
                      <IconMoreFilled />
                    </Icon>
                  </IconButton>
                }
                data-test="file-actions"
              >
                <Menu.Item onClick={onDeleteClick} data-test="trigger-file-delete">
                  <Menu.ItemIcon render={<IconDeleteOutline />} />
                  <Menu.ItemLabel>Delete</Menu.ItemLabel>
                </Menu.Item>
                <Menu.Item onClick={onRenameClick} data-test="trigger-file-rename">
                  <Menu.ItemIcon render={<IconRenameFilled />} />
                  <Menu.ItemLabel>Rename</Menu.ItemLabel>
                </Menu.Item>
              </Menu>
            )}
          </Box>
        </Box>
      </Card>
    </div>
  );
}

export default UploadedLeaseControls;
